import AreaChart from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/AreaChart/AreaChart";
import { NoEventDataMessage } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/NoEventDataMessage";
import { SectionStateContent } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/SectionStateContent";
import { InfoContent } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/TransportNeed/infoContent";
import DecisionSupportContext from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/DecisionSupportContext";
import { AltTransStretchCoverage } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/types";
import { useContext, useMemo } from "react";
import { Circle } from "shared/components/Circle";
import { InfoHeader } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/InfoHeader";
import { Flex, Text, tokens, VStack } from "shared/components/Spor";
import RouteSvg from "shared/icons/route.svg?react";
import { useTheme } from "styled-components";

type TransportNeedProps = {
  selectedStretch: AltTransStretchCoverage | null;
  showAllStretches: boolean;
};

export const TransportNeed = ({
  selectedStretch,
  showAllStretches,
}: TransportNeedProps) => {
  const theme = useTheme();

  const {
    transportNeedData,
    isTransportNeedPending,
    isTransportNeedError,
    refetchTransportNeed,
  } = useContext(DecisionSupportContext);

  const isLoading = isTransportNeedPending;
  const isError = isTransportNeedError;
  const isEmpty = !isLoading && !isError && transportNeedData.length === 0;

  const displayedStretchName = selectedStretch?.stretchName;

  const chosenStretchData = useMemo(() => {
    if (!displayedStretchName) {
      return [];
    }

    // 1) Exact match if possible
    const exactMatches = transportNeedData.filter(
      (d) => d.stretch === displayedStretchName,
    );
    if (exactMatches.length > 0) {
      return exactMatches;
    }

    // 2) Fuzzy match on destination part (after "–"), case-insensitive
    const [, titleToRaw = ""] = displayedStretchName.split("–");
    const titleTo = titleToRaw.trim().toLowerCase();

    const fuzzyMatches = transportNeedData.filter((d) => {
      const [, stretchToRaw = ""] = d.stretch.split("–");
      const stretchTo = stretchToRaw.trim().toLowerCase();
      return stretchTo !== "" && stretchTo === titleTo;
    });

    if (fuzzyMatches.length > 0) {
      return fuzzyMatches;
    }

    // 3) Fallback: nothing matched
    return [];
  }, [displayedStretchName, transportNeedData]);

  const totalEventData = useMemo(() => {
    const sumsByDateMs = transportNeedData.reduce<Record<string, number>>(
      (acc, d) => {
        const key = String(d.date.getTime());
        acc[key] = (acc[key] ?? 0) + (d.passengers ?? 0);
        return acc;
      },
      {},
    );

    return Object.entries(sumsByDateMs)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([dateMs, passengers]) => ({
        stretch: "Totalt",
        date: new Date(Number(dateMs)),
        passengers,
      }));
  }, [transportNeedData]);

  const effectiveMaxYValue = useMemo(
    () => Math.max(0, ...totalEventData.map((d) => d.passengers ?? 0)),
    [totalEventData],
  );

  return (
    <VStack
      position="relative"
      gap={tokens.default.size.spacing[2]}
      backgroundColor={theme.colorBackgroundSecondary}
      padding={tokens.default.size.spacing[2]}
      borderRadius={tokens.default.size.spacing[1]}
      height="100%"
      flex="1"
      overflow="hidden"
    >
      <InfoHeader
        title="Maksimalt transportbehov"
        iconColor={theme.colorIconSecondary}
        alignment="right"
        tooltipWidth="360px"
        headerContent={
          <Flex direction="row" alignItems="center">
            {selectedStretch && (
              <>
                <RouteSvg />
                <Text
                  ml={tokens.default.size.spacing[1]}
                  mr={tokens.default.size.spacing[2]}
                  variant="xs"
                  color={theme.colorTextSecondary}
                >
                  {displayedStretchName}
                </Text>
              </>
            )}
            <Circle size="12px" bg={theme.colorSuccessPulse} />
            <Text
              mx={tokens.default.size.spacing[1]}
              variant="xs"
              color={theme.colorTextSecondary}
            >
              Totalt brudd
            </Text>
          </Flex>
        }
      >
        <InfoContent />
      </InfoHeader>
      <SectionStateContent
        isLoading={isLoading}
        isError={isError}
        isEmpty={isEmpty}
        onRetry={refetchTransportNeed}
        emptyContent={<NoEventDataMessage mt="-2" />}
      >
        <AreaChart
          chosenStretchData={chosenStretchData}
          totalEventData={totalEventData}
          maxYValue={effectiveMaxYValue}
          hasResponsiveHeight={showAllStretches}
        />
      </SectionStateContent>
    </VStack>
  );
};
